import { useQuery, useTRPC } from "@luckymenu/api-client/react";
import type { Permission, PlanFeature } from "@luckymenu/core";
import { type NavigationModel, navigationFor, planIncludes } from "../navigation";
import { useSupportSession } from "../provider";
import { useCurrentMembership } from "./use-current";

/**
 * Tabs and More items for the caller's role in the active restaurant. Both
 * apps render their navigation from this. "Switch restaurant" appears only
 * with more than one restaurant, and never in a support session.
 */
export function useNavigationModel(): NavigationModel | null {
	const trpc = useTRPC();
	const current = useCurrentMembership();
	const support = useSupportSession((s) => s.session);
	const list = useQuery({
		...trpc.staff.tenancy.listMyRestaurants.queryOptions(),
		enabled: support === null,
	});
	if (!current.data) return null;
	return navigationFor(current.data.role, {
		restaurantCount: support ? 1 : (list.data?.length ?? 1),
		features: current.data.entitlements?.features,
	});
}

/** Whether the caller's role has `permission` here. False until the membership has loaded. */
export function useCan(permission: Permission): boolean {
	const current = useCurrentMembership();
	return current.data?.permissions.includes(permission) ?? false;
}

/**
 * Whether the restaurant's plan includes one of `features` (P10-T04). True
 * until the membership has loaded, so nothing flickers away; the server
 * refuses what the plan lacks anyway (`not_in_plan`).
 */
export function usePlanIncludes(features: PlanFeature | readonly PlanFeature[]): boolean {
	const current = useCurrentMembership();
	return planIncludes(current.data?.entitlements?.features, features);
}
